import { Controller } from "@hotwired/stimulus"
import { MEDIA_CAP, screenFiles, pastedFiles } from "./media_rules"

// THE conversation page (C6, 2026-09-04: "just like iMessage"). The page
// IS the composer: an editable To: line of chips at the top, the thread
// (or nothing) in a frame beneath it, the message box at the bottom. New
// message is this page with nobody in To:. Every change to the set asks
// the server which thread it is (/lookup, the composer's own rule): a set
// with history swaps its thread in, a new set shows blank, and the first
// send creates it. Three endpoints (/people, /lookup, /compose) and one
// add door (/add_person), all behind PhoneHolder and ConversationComposer.
//
// The rules that came out of the owner's first two minutes with it:
//   1. Whatever you type becomes a token. A number chips on Enter, Tab,
//      comma, blur, or the moment you click into the message box, and the
//      chip carries the server's answer: a refused chip is red with its
//      sentence under To:. The picker never shows a dead row.
//   2. Send is never silent. whyNotReady() returns the next step as a
//      sentence, the status line shows it, and Send is disabled exactly
//      while there is one.
//   3. Enter picks what the eye sees: the first result is highlighted.
// Nothing is confirmed: the sidebar's draft row, the title and the empty
// state already say where a send lands, as Messages does.
const MAX_PEOPLE = 8

export default class extends Controller {
  static targets = ["chips", "input", "results", "groupNote", "frame",
                    "add", "addName", "addPhone", "addRole", "addCompany", "companyField", "addNote", "saveNew",
                    "segNew", "segExisting", "existing", "newFields", "who", "whoResults", "whoChosen", "addLabel", "addPhoneExisting",
                    "text", "tray", "size", "error", "files", "status", "send", "message"]
  static values = { peopleUrl: String, composeUrl: String, addUrl: String, lookupUrl: String, newUrl: String,
                    canSend: Boolean, desktop: String, noOffice: String, mediaTypes: Array, mediaSentence: String,
                    people: Array, thread: String, openAdd: Boolean }

  connect() {
    this.people = new Map()   // token -> row {token, kind, name, pretty, detail, state, reason, resolving}
    this.filesList = []
    this.dropboxFiles = []
    this.rows = []
    this.active = -1
    this.inFlight = false
    this.mq = window.matchMedia(this.desktopValue)
    // The page's own set: a thread's people, or the ones the URL names.
    for (const r of this.peopleValue) this.people.set(r.token, r)
    this.onChosen = (e) => { if (e.detail?.owner === "conversation") this.addDropboxFiles(e.detail.rows || []) }
    document.addEventListener("files-picker:chosen", this.onChosen)
    this.restoreDraft()
    this.render()
    // The frame is the thread: when it lands, the title follows the set
    // and the newest text is in view.
    this.onFrame = () => this.frameLanded()
    this.element.addEventListener("turbo:frame-load", this.onFrame)
    this.frameLanded()
    if (this.openAddValue) this.openAdd({})
    else if (this.people.size === 0) requestAnimationFrame(() => this.inputTarget.focus())
    else if (this.threadValue && this.mq.matches) requestAnimationFrame(() => this.textTarget.focus())
  }

  disconnect() {
    document.removeEventListener("files-picker:chosen", this.onChosen)
    this.element.removeEventListener("turbo:frame-load", this.onFrame)
  }

  frameLanded() {
    const list = this.element.querySelector("#messages-container")
    if (list) list.scrollTop = list.scrollHeight
    const title = this.element.querySelector("[data-role='thread-context']")?.dataset.title
    if (!title) return
    document.title = title
    const shell = document.querySelector(".uds-shell-title")
    if (shell) shell.textContent = title
  }

  // ---- the set decides the page ----
  // Every change to who is in To: asks the server which thread this is.
  // A set with history: the frame shows it and the URL becomes its key.
  // A new set: the frame shows nothing and the URL names the people, so
  // reload and back rebuild the chips. Keys are never computed here.
  async resolveSet() {
    this.render()
    const tokens = [...this.people.values()].filter((r) => r.state === "ok" && !r.resolving).map((r) => r.token)
    const stamp = tokens.join(",")
    if (stamp === this.lastStamp) return
    this.lastStamp = stamp
    this.setRefusal = null
    if (tokens.length === 0) { this.swap(this.newUrlValue); this.syncList({}); return }
    try {
      const qs = tokens.map((t) => `recipients[]=${encodeURIComponent(t)}`).join("&")
      const res = await fetch(`${this.lookupUrlValue}?${qs}`, { headers: { Accept: "application/json" } })
      const data = await res.json().catch(() => ({}))
      if (stamp !== this.lastStamp) return
      if (!res.ok) { this.setRefusal = data.error || "Could not check these people."; this.render(); return }
      this.setRefusal = null
      this.swap(data.url)
      this.syncList(data)
    } catch (_e) { this.flash("Could not reach the server. Try again.") }
  }

  // The sidebar follows the set, as Messages' does: a new set puts its
  // draft row (server-rendered) at the top, selected; an existing set
  // lights that thread's row; nobody shows the blank "New Message" row.
  syncList(data) {
    const list = document.getElementById("conversation-list")
    if (!list) return
    list.querySelectorAll("[data-draft]:not([hidden])").forEach((el) => el.remove())
    list.querySelectorAll(".cv-row.is-active").forEach((el) => el.classList.remove("is-active"))
    if (data.row) {
      list.insertAdjacentHTML("afterbegin", data.row)
    } else if (data.existing) {
      list.querySelector(`[data-thread="${data.key}"]`)?.classList.add("is-active")
    } else {
      const blank = list.querySelector("[data-draft][hidden]")
      if (blank) { const row = blank.cloneNode(true); row.hidden = false; row.classList.add("is-active"); list.prepend(row) }
    }
  }

  swap(url) {
    // The same URL again (a chip became the person it names): the frame
    // re-renders so the context line and the title follow.
    if (this.hasFrameTarget) {
      if (this.frameTarget.getAttribute("src") === url) this.frameTarget.reload()
      else this.frameTarget.src = url
    }
    if (window.location.pathname + window.location.search !== url) history.replaceState(history.state, "", url)
    this.restoreDraft()
  }

  // ---- drafts: a message survives leaving the page, per set ----
  draftKey() { return `sms-draft:${window.location.pathname}${window.location.search}` }
  saveDraft() { try { const v = this.textTarget.value; v ? sessionStorage.setItem(this.draftKey(), v) : sessionStorage.removeItem(this.draftKey()) } catch (_e) {} }
  restoreDraft() {
    try {
      const v = sessionStorage.getItem(this.draftKey())
      if (v && !this.textTarget.value) { this.textTarget.value = v; this.grow() }
    } catch (_e) {}
  }

  focusInput() { this.inputTarget.focus() }

  // ---- picker ----
  search() {
    clearTimeout(this.timer)
    const q = this.inputTarget.value.trim()
    this.render()
    if (q.length < 2 && q.replace(/\D/g, "").length < 3) { this.renderResults([]); return }
    this.timer = setTimeout(() => this.fetchPeople(q).then((rows) => { if (rows && this.inputTarget.value.trim() === q) this.renderResults(rows) }), 150)
  }

  async fetchPeople(q) {
    try {
      const res = await fetch(`${this.peopleUrlValue}?q=${encodeURIComponent(q)}`, { headers: { Accept: "application/json" } })
      if (!res.ok) return null
      return (await res.json()).rows || []
    } catch (_e) { return null }
  }

  // Every row shows the person as the server labels them (name and context,
  // or a number) with the number beneath. A number nobody holds carries the
  // add door beside it; nobody ever reads "no one saved" for digits.
  renderResults(rows) {
    this.rows = rows.filter((r) => !this.people.has(r.token))
    this.active = this.rows.length ? 0 : -1
    const q = this.inputTarget.value.trim()
    const digits = q.replace(/\D/g, "")
    if (this.rows.length === 0) {
      if (!q) this.resultsTarget.innerHTML = ""
      else if (/^[\d\s().+-]+$/.test(q)) this.resultsTarget.innerHTML = `<li class="cw-results-empty">Keep typing the number.</li>`
      else this.resultsTarget.innerHTML = `<li class="cw-results-empty">No one in Contacts named that.</li>
        <li><button type="button" class="cw-row is-add" data-name="${this.esc(q)}" data-action="conversation#addFromRow">
          <span class="cw-row-avatar">+</span><span class="cw-row-text"><span class="cw-row-name">Add to Contacts</span><span class="cw-row-detail">Name, client, and what they do</span></span></button></li>`
      this.render()
      return
    }
    this.resultsTarget.innerHTML = this.rows.map((r, i) => `
      <li role="option" class="cw-item"><button type="button" class="cw-row is-${r.kind} ${i === 0 ? "is-active" : ""} ${r.state === "refused" ? "is-refused" : ""}" data-index="${i}" data-action="conversation#pick">
        <span class="cw-row-avatar">${r.kind === "number" ? "#" : this.initials(r.name)}</span>
        <span class="cw-row-text">
          <span class="cw-row-name">${this.label(r)}</span>
          <span class="cw-row-detail ${r.state === "refused" ? "is-refused" : ""}">${this.esc(r.detail || "")}</span>
          ${r.note ? `<span class="cw-row-note">${this.esc(r.note)}</span>` : ""}
        </span>
      </button>${r.action === "add" ? `<button type="button" class="cw-pill" aria-label="Add ${this.esc(r.name)} to Contacts" data-phone="${this.esc(r.e164)}" data-action="conversation#addFromRow">Add to Contacts</button>` : ""}</li>`).join("")
    this.render() // the status line names the lit row ("Press Enter to add Hunter Scott.")
  }

  label(r) {
    return this.esc(r.name) + (r.context ? ` <span class="cw-row-context">(${this.esc(r.context)})</span>` : "")
  }

  pick(event) {
    const row = this.rows[Number(event.currentTarget.dataset.index)]
    if (row) this.take(row)
    this.inputTarget.focus()
  }

  // Adds a person. Focus is the caller's business: a mouse pick returns it
  // to To:, Enter is already there, and leaving To: for the message box
  // must stay left (the owner's first click bounced back here once).
  take(row) {
    if (this.people.size >= MAX_PEOPLE) { this.flash(`A group text can have at most ${MAX_PEOPLE} people.`); return }
    this.people.set(row.token, row)
    this.inputTarget.value = ""
    this.renderResults([])
    this.errorTarget.textContent = ""
    this.resolveSet()
  }

  // A mouse pick must not blur To: first: the blur would commit the
  // highlighted row before the click lands on the one under the pointer.
  holdFocus(event) { event.preventDefault() }

  // Whatever is typed becomes a token. A highlighted result is taken; a
  // whole phone number chips at once and the server settles what it is.
  commitTyped() {
    const raw = this.inputTarget.value.trim()
    if (raw === "") return false
    if (this.active >= 0 && this.rows[this.active]) { this.take(this.rows[this.active]); return true }
    const digits = raw.replace(/\D/g, "")
    if (digits.length !== 10 && !(digits.length === 11 && digits.startsWith("1"))) return false
    const token = `number:${raw}`
    if (this.people.has(token)) { this.inputTarget.value = ""; return true }
    const chip = { token, kind: "number", name: this.pretty(raw), pretty: this.pretty(raw), state: "ok", resolving: true }
    this.take(chip)
    this.fetchPeople(raw).then((rows) => {
      const found = (rows || []).find((r) => r.pretty === chip.pretty) || { ...chip, state: "refused", reason: "Could not check this number. Try again." }
      if (!this.people.has(token)) return
      this.people.delete(token)
      this.people.set(found.token, { ...found, resolving: false })
      this.resolveSet()
    })
    return true
  }

  inputBlur() { setTimeout(() => this.commitTyped(), 0) }

  inputKeydown(event) {
    const buttons = [...this.resultsTarget.querySelectorAll("button.cw-row:not(.is-add)")]
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      if (buttons.length === 0) return
      this.active = event.key === "ArrowDown" ? Math.min(this.active + 1, buttons.length - 1) : Math.max(this.active - 1, 0)
      buttons.forEach((b, i) => b.classList.toggle("is-active", i === this.active))
      buttons[this.active].scrollIntoView({ block: "nearest" })
    } else if (event.key === "Enter" || event.key === "," || (event.key === "Tab" && this.inputTarget.value.trim() !== "")) {
      if (event.isComposing) return
      // A comma or Tab that commits nothing stays a comma or a Tab ("Smith, J").
      if (this.commitTyped() || event.key === "Enter") event.preventDefault()
    } else if (event.key === "Backspace" && this.inputTarget.value === "" && this.people.size) {
      this.people.delete([...this.people.keys()].pop())
      this.resolveSet()
    }
  }

  removeChip(event) {
    this.people.delete(event.currentTarget.dataset.token)
    this.resolveSet()
    this.inputTarget.focus()
  }

  // ---- Add to Contacts: the one add panel, one door (add_person) ----
  toggleAdd() {
    if (this.addTarget.classList.contains("is-open")) { this.addTarget.classList.remove("is-open"); this.render(); return }
    this.openAdd({})
  }

  // Opens the panel with what is known: a number (from a row, a chip or a
  // thread header) or a typed name.
  openAdd({ phone = "", name = "" } = {}) {
    const typed = this.inputTarget.value.trim()
    const digits = typed.replace(/\D/g, "")
    if (!phone && digits.length >= 10) phone = typed
    if (!name && typed && digits.length < 10) name = typed
    // A number that arrived with the request is a fact, shown pretty and
    // not editable; a typed one stays editable.
    if (phone) { this.addPhoneTarget.value = this.pretty(phone); this.addPhoneTarget.readOnly = true } else { this.addPhoneTarget.readOnly = false }
    if (name) this.addNameTarget.value = name
    this.inputTarget.value = ""
    this.renderResults([])
    this.addTarget.classList.add("is-open")
    this.setAddMode("new")
    this.render()
    ;(this.addNameTarget.value ? this.addPhoneTarget : this.addNameTarget).focus()
  }

  addFromRow(event) {
    const { phone, name } = event.currentTarget.dataset
    this.openAdd({ phone: phone || "", name: name || "" })
  }

  addFromChip(event) {
    this.openAdd({ phone: event.currentTarget.dataset.phone })
    if (event.currentTarget.dataset.mode === "existing") this.setAddMode("existing")
  }

  // ---- Existing contact: the number joins someone already saved ----
  addMode(event) { this.setAddMode(event.currentTarget.dataset.mode) }

  setAddMode(mode) {
    this.mode = mode
    const existing = mode === "existing"
    this.segNewTarget.classList.toggle("is-on", !existing)
    this.segExistingTarget.classList.toggle("is-on", existing)
    this.segNewTarget.setAttribute("aria-selected", String(!existing))
    this.segExistingTarget.setAttribute("aria-selected", String(existing))
    this.existingTarget.hidden = !existing
    this.newFieldsTarget.hidden = existing
    this.addPhoneExistingTarget.value = this.addPhoneTarget.value
    this.addNote("")
    if (existing) { this.target = null; this.whoChosenTarget.textContent = ""; this.whoTarget.focus() }
  }

  searchWho() {
    clearTimeout(this.whoTimer)
    const q = this.whoTarget.value.trim()
    this.target = null
    this.whoChosenTarget.textContent = ""
    if (q.length < 2) { this.whoResultsTarget.innerHTML = ""; return }
    this.whoTimer = setTimeout(() => this.fetchPeople(q).then((rows) => {
      if (!rows || this.whoTarget.value.trim() !== q) return
      // People with a record only, once each: a number is not someone,
      // and staff numbers are edited in Users.
      const seen = new Set()
      this.whoRows = rows.filter((r) => (r.kind === "contact" || r.kind === "external") && !seen.has(r.token.split(":").slice(0, 2).join(":")) && seen.add(r.token.split(":").slice(0, 2).join(":")))
      this.whoResultsTarget.innerHTML = this.whoRows.length === 0
        ? `<li class="cw-results-empty">No one in Contacts named that.</li>`
        : this.whoRows.map((r, i) => `<li role="option" class="cw-item"><button type="button" class="cw-row is-${r.kind} ${i === 0 ? "is-active" : ""}" data-index="${i}" data-action="conversation#pickWho">
            <span class="cw-row-avatar">${this.initials(r.name)}</span><span class="cw-row-text"><span class="cw-row-name">${this.label(r)}</span><span class="cw-row-detail">${this.esc(r.detail || "")}</span></span></button></li>`).join("")
    }), 150)
  }

  whoKeydown(event) {
    if (event.key === "Enter" && this.whoRows && this.whoRows[0]) { event.preventDefault(); this.chooseWho(this.whoRows[0]) }
  }

  pickWho(event) { this.chooseWho(this.whoRows[Number(event.currentTarget.dataset.index)]) }

  chooseWho(row) {
    this.target = row.token.split(":").slice(0, 2).join(":")
    this.whoTarget.value = row.name
    this.whoResultsTarget.innerHTML = ""
    this.whoChosenTarget.textContent = `Adding ${this.addPhoneExistingTarget.value} to ${row.name}${row.context ? ` (${row.context})` : ""}.`
    this.addLabelTarget.focus()
  }

  clientPicked(event) {
    if (this.hasCompanyFieldTarget) this.companyFieldTarget.hidden = !!event.detail?.id
  }

  async saveNew() {
    const existing = this.mode === "existing"
    const body = existing
      ? { phone: this.addPhoneExistingTarget.value.trim(), target: this.target || "", label: this.addLabelTarget.value }
      : { name: this.addNameTarget.value.trim(), phone: this.addPhoneTarget.value.trim(),
          company: this.addCompanyTarget.value.trim(), role_label: this.addRoleTarget.value.trim(),
          client_id: this.addTarget.querySelector('input[name="client_id"]')?.value || "" }
    if (existing && !body.target) { this.addNote("Pick who this number belongs to.", true); return }
    if (!existing && (!body.name || !body.phone)) { this.addNote("Enter a name and a phone number.", true); return }
    this.saveNewTarget.disabled = true
    try {
      const res = await fetch(this.addUrlValue, { method: "POST", headers: this.headers(), body: JSON.stringify(body) })
      const data = await res.json()
      if (!res.ok) { this.addNote(data.error || "Could not save them.", true); return }
      // A number chip for this person becomes the person, and the frame
      // re-renders the thread under their name.
      // Any chip on that number IS the person now (a bare number, or a
      // thread's unnamed record that just got a name or joined someone).
      for (const [token, row] of this.people) if (row.pretty === data.row.pretty) this.people.delete(token)
      this.lastStamp = null
      this.take(data.row)
      if (data.row.note) this.groupNoteTarget.textContent = data.row.note
      ;[this.addNameTarget, this.addPhoneTarget, this.addCompanyTarget, this.addRoleTarget, this.whoTarget].forEach((el) => { el.value = "" })
      this.addPhoneTarget.readOnly = false
      this.setAddMode("new")
      this.addNote("")
      this.addTarget.classList.remove("is-open")
      this.render()
      this.textTarget.focus()
    } catch (_e) {
      this.addNote("Could not reach the server. Try again.", true)
    } finally {
      this.saveNewTarget.disabled = false
    }
  }

  addNote(text, error = false) {
    this.addNoteTarget.textContent = text
    this.addNoteTarget.classList.toggle("is-error", error)
  }

  // ---- message + attachments ----
  grow() {
    const el = this.textTarget
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, window.innerHeight * 0.4) + "px"
    this.saveDraft()
    this.render()
  }

  textKeydown(event) {
    // Desktop: Enter sends, Shift+Enter is a newline. Phone: Enter is a newline.
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing && this.mq.matches) {
      event.preventDefault()
      this.send()
    }
  }

  filesChanged() {
    this.addFiles(this.filesTarget.files)
    this.filesTarget.value = ""
  }

  // ONE door for a file, however it arrives: the picker, a drop, a paste.
  // A file outside the list is refused by name at once, never at send time.
  addFiles(files) {
    const { ok, note } = screenFiles(files, this.mediaTypesValue, this.mediaSentenceValue)
    this.filesList.push(...ok)
    this.errorTarget.textContent = note
    this.render()
  }

  dragOver(event) {
    event.preventDefault()
    this.messageTarget.classList.add("is-over")
  }

  dragLeave(event) {
    if (!this.messageTarget.contains(event.relatedTarget)) this.messageTarget.classList.remove("is-over")
  }

  drop(event) {
    event.preventDefault()
    this.messageTarget.classList.remove("is-over")
    this.addFiles(event.dataTransfer.files)
    this.textTarget.focus()
  }

  paste(event) {
    const files = pastedFiles(event)
    if (files.length) { event.preventDefault(); this.addFiles(files) }
  }

  removeFile(event) {
    this.filesList.splice(Number(event.currentTarget.dataset.index), 1)
    this.render()
  }

  // ---- TREND Files ----
  openFiles(event) {
    event.preventDefault()
    document.dispatchEvent(new CustomEvent("files-picker:open", { detail: { owner: "conversation", opener: event.currentTarget } }))
  }

  addDropboxFiles(rows) {
    for (const r of rows) if (!this.dropboxFiles.some((f) => f.id === r.id)) this.dropboxFiles.push(r)
    this.render()
    this.textTarget.focus()
  }

  removeDropboxFile(event) {
    this.dropboxFiles.splice(Number(event.currentTarget.dataset.index), 1)
    this.render()
  }

  totalBytes() { return this.filesList.reduce((n, f) => n + f.size, 0) + this.dropboxFiles.reduce((n, f) => n + (f.size || 0), 0) }
  attachmentCount() { return this.filesList.length + this.dropboxFiles.length }

  // ---- send ----
  async send() {
    if (this.inFlight || this.whyNotReady()) return
    const fd = new FormData()
    for (const token of this.people.keys()) fd.append("recipients[]", token)
    fd.append("content", this.textTarget.value)
    for (const f of this.filesList) fd.append("attachments[]", f, f.name)
    for (const f of this.dropboxFiles) fd.append("dropbox_file_ids[]", f.id)

    this.lock(true)
    try {
      const res = await fetch(this.composeUrlValue, { method: "POST", headers: { Accept: "application/json", "X-CSRF-Token": this.csrf() }, body: fd })
      if (res.redirected) { this.lock(false); this.flash("Your session ended. Reload and sign in again. Your message is still here."); return }
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.url) {
        try { sessionStorage.removeItem(this.draftKey()) } catch (_e) {}
        window.Turbo ? window.Turbo.visit(data.url) : (window.location.href = data.url)
        return
      }
      this.lock(false)
      this.flash(data.error || "Could not send. Try again.")
    } catch (_e) {
      this.lock(false)
      this.flash("Could not reach the server. Your message is still here. Try again.")
    }
  }

  lock(on) {
    this.inFlight = on
    this.sendTarget.classList.toggle("is-sending", on)
    this.sendTarget.textContent = on ? "Sending..." : "Send"
    this.render()
  }

  // The next step as a sentence, or null when the message can go. This is
  // the ONE rule behind the status line and the Send button.
  whyNotReady() {
    if (!this.canSendValue) return this.noOfficeValue
    if (this.inFlight) return "Sending..."
    const refused = [...this.people.values()].find((r) => r.state === "refused")
    if (refused) return refused.reason || "That number cannot be texted."
    if (this.setRefusal) return this.setRefusal
    const resolving = [...this.people.values()].find((r) => r.resolving)
    if (resolving) return `Checking ${resolving.pretty}...`
    // Whatever sits uncommitted in To: comes first, with or without chips:
    // a typed second person must never vanish under a lit Send button.
    const typed = this.inputTarget.value.trim()
    if (typed) {
      const lit = this.rows[this.active]
      if (lit && lit.kind === "number") return `Press Enter to add ${lit.name}, or Add to Contacts.`
      if (lit) return `Press Enter to add ${lit.name}.`
      const digits = typed.replace(/\D/g, "")
      if (digits.length === 10 || (digits.length === 11 && digits.startsWith("1"))) return `Press Enter to add ${this.pretty(typed)}, or Add to Contacts.`
      return "Pick a person from the list, or type a phone number."
    }
    if (this.people.size === 0) return "Add someone in To: to send."
    if (this.textTarget.value.trim() === "" && this.attachmentCount() === 0) return "Type a message or add a photo."
    if (this.attachmentCount() && this.totalBytes() >= MEDIA_CAP) return "Attachments must be under 1 MB together. Remove one to send."
    return null
  }

  // ---- render ----
  render() {
    this.chipsTarget.innerHTML = [...this.people.values()].map((r) => {
      const cls = [r.kind === "external" || r.kind === "number" ? "is-outside" : "", r.state === "refused" ? "is-refused" : "", r.resolving ? "is-resolving" : ""].join(" ")
      return `<span class="cw-chip ${cls}">
        <span class="cw-chip-avatar">${r.kind === "number" ? "#" : this.initials(r.name)}</span><span class="cw-chip-name">${this.esc(r.name)}${r.context ? ` <span class="cw-chip-context">(${this.esc(r.context)})</span>` : ""}</span>
        <button type="button" class="cw-chip-x" aria-label="Remove ${this.esc(r.name)}" data-token="${this.esc(r.token)}" data-action="conversation#removeChip">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
        </button></span>`
    }).join("")
    // The group note is the count; a "note" from the add door ("Already
    // saved as X.") rides the same line until the set changes.
    const n = this.people.size
    const unsaved = [...this.people.values()].find((r) => r.state === "ok" && !r.resolving && (r.kind === "number" || r.unnamed))
    const kept = this.groupNoteTarget.textContent.startsWith("Already") ? this.groupNoteTarget.textContent : ""
    const door = unsaved ? `${this.esc(unsaved.pretty)} isn't in Contacts. <button type="button" class="cw-link" data-phone="${this.esc(unsaved.e164 || unsaved.token.slice(7))}" data-action="conversation#addFromChip">Add to Contacts</button> &middot; <button type="button" class="cw-link" data-phone="${this.esc(unsaved.e164 || unsaved.token.slice(7))}" data-mode="existing" data-action="conversation#addFromChip">Add to existing contact</button>` : ""
    if (this.setRefusal) this.groupNoteTarget.textContent = this.setRefusal
    else if (n > 1) this.groupNoteTarget.innerHTML = `Group of ${n}${door ? "<br>" + door : ""}`
    else if (n === 1 && unsaved) this.groupNoteTarget.innerHTML = door
    else this.groupNoteTarget.textContent = n === 1 ? kept : ""
    this.trayTarget.innerHTML = this.filesList.map((f, i) => {
      const url = URL.createObjectURL(f)
      const media = f.type.startsWith("video/") ? `<video src="${url}" muted></video>`
        : f.type === "application/pdf" ? `<span class="cw-thumb-doc">PDF</span>` : `<img src="${url}" alt="">`
      return `<span class="cw-thumb" title="${this.esc(f.name)}">${media}<button type="button" aria-label="Remove ${this.esc(f.name)}" data-index="${i}" data-action="conversation#removeFile">&times;</button></span>`
    }).join("") + this.dropboxFiles.map((f, i) => `<span class="cw-thumb is-dropbox" title="${this.esc(f.name)} (TREND Files)"><span class="cw-thumb-doc">${this.esc(f.name.split(".").pop().toUpperCase().slice(0, 4))}</span><button type="button" aria-label="Remove ${this.esc(f.name)}" data-index="${i}" data-action="conversation#removeDropboxFile">&times;</button></span>`).join("")
    if (this.attachmentCount()) {
      const mb = (Math.floor(this.totalBytes() / 10_000) / 100).toFixed(2)
      const over = this.totalBytes() >= MEDIA_CAP
      this.sizeTarget.textContent = over ? `Attachments must be under 1 MB together. This is ${mb} MB.` : `${mb} MB of 1 MB`
      this.sizeTarget.classList.toggle("is-over", over)
    } else {
      this.sizeTarget.textContent = ""
      this.sizeTarget.classList.remove("is-over")
    }
    const why = this.whyNotReady()
    this.statusTarget.textContent = why || (this.mq.matches ? "Enter sends. Shift+Enter for a new line." : "")
    this.statusTarget.classList.toggle("is-blocking", !!why && why !== "Sending..." && this.people.size > 0)
    this.sendTarget.disabled = !!why
  }

  flash(text) { this.errorTarget.textContent = text }
  headers() { return { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrf() } }
  csrf() { return document.querySelector('meta[name="csrf-token"]')?.content || "" }
  initials(name) { return /^[(+\d]/.test(name || "") ? "#" : (name || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase() }
  pretty(raw) { const d = raw.replace(/\D/g, "").slice(-10); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : raw }
  esc(s) { const d = document.createElement("div"); d.textContent = s == null ? "" : String(s); return d.innerHTML.replace(/"/g, "&quot;") }
}
